import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Switch,
  FormControlLabel,
  TextField,
  Button,
  Alert,
  Divider,
  CircularProgress
} from '@mui/material';
import {
  SwapHoriz as SwapIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import { apiRequest } from '../config/api';
import Layout from '../components/Layout';
import { SwapConfiguration } from '../types';

const PartnershipSwapSettings: React.FC = () => {
  const [config, setConfig] = useState<SwapConfiguration>({
    enabled: true,
    lookbackLeagueDays: 5
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    fetchConfiguration();
  }, []);

  const fetchConfiguration = async () => {
    try {
      setLoading(true);
      const response = await apiRequest('/api/league-days/swap-config');
      if (response.ok) {
        const data = await response.json();
        setConfig(data);
      } else {
        setError('Failed to load configuration');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const response = await apiRequest('/api/league-days/swap-config', {
        method: 'PUT',
        body: JSON.stringify(config)
      });

      if (response.ok) {
        const updated = await response.json();
        setConfig(updated);
        setSuccess('Configuration saved successfully!');
        setTimeout(() => setSuccess(null), 3000);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to save configuration');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box maxWidth="800px" mx="auto">
        <Box mb={3}>
          <Typography variant="h4" gutterBottom>
            Partnership Swap Settings
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Configure automatic partnership swapping to avoid recent repeat pairings
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" mb={3}>
              <SwapIcon color="primary" sx={{ mr: 1, fontSize: 32 }} />
              <Typography variant="h6">
                Post-Shuffle Partnership Management
              </Typography>
            </Box>

            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                How it works:
              </Typography>
              <Typography variant="body2" component="div">
                1. Players are randomly shuffled and assigned numbers<br />
                2. System checks the last N completed league days for repeat partnerships<br />
                3. <strong>Conflict-Aware Smart Swapping:</strong><br />
                   &nbsp;&nbsp;&nbsp;• Prioritizes partnerships by <strong>frequency</strong> (most repeated first)<br />
                   &nbsp;&nbsp;&nbsp;• Among equal frequency, resolves <strong>most recent</strong> first<br />
                   &nbsp;&nbsp;&nbsp;• <strong>Unlimited swaps</strong> - continues until no more improvements possible<br />
                   &nbsp;&nbsp;&nbsp;• Verifies each swap doesn't create new conflicts before accepting<br />
                4. Full audit trail is stored and displayed for transparency
              </Typography>
            </Alert>

            <Divider sx={{ my: 3 }} />

            {/* Enable/Disable */}
            <Box mb={3}>
              <FormControlLabel
                control={
                  <Switch
                    checked={config.enabled}
                    onChange={(e) => setConfig({ ...config, enabled: e.target.checked })}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography variant="subtitle1">
                      Enable Partnership Swap System
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Automatically swap player numbers to avoid recent repeat partnerships
                    </Typography>
                  </Box>
                }
              />
            </Box>

            {config.enabled && (
              <>
                <Divider sx={{ my: 3 }} />

                {/* Lookback League Days */}
                <Box mb={3}>
                  <Typography variant="subtitle1" gutterBottom>
                    Lookback League Days
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={2}>
                    Number of recent completed league days to check for repeat partnerships (regardless of how old they are)
                  </Typography>
                  <TextField
                    type="number"
                    value={config.lookbackLeagueDays}
                    onChange={(e) => setConfig({ ...config, lookbackLeagueDays: parseInt(e.target.value) || 1 })}
                    fullWidth
                    inputProps={{ min: 1, max: 10 }}
                  />
                </Box>
              </>
            )}

            <Divider sx={{ my: 3 }} />

            {/* Save Button */}
            <Box display="flex" justifyContent="flex-end">
              <Button
                variant="contained"
                color="primary"
                startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Configuration'}
              </Button>
            </Box>
          </CardContent>
        </Card>

        {/* Additional Info */}
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Benefits of This System
            </Typography>
            <Box component="ul" sx={{ pl: 2 }}>
              <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                ✅ <strong>Maintains randomness</strong>: Original assignment is truly random
              </Typography>
              <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                ✅ <strong>Fully transparent</strong>: Players see exactly what happened and why
              </Typography>
              <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                ✅ <strong>Auditable</strong>: Complete history stored in database
              </Typography>
              <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                ✅ <strong>Smart & Conflict-Aware</strong>: Unlimited swaps until all repeats resolved or no valid swap exists
              </Typography>
              <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                ✅ <strong>No New Conflicts</strong>: Verifies swaps don't create new repeat partnerships
              </Typography>
              <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                ✅ <strong>Fair</strong>: Prioritizes most frequent repeats first, then most recent
              </Typography>
              <Typography component="li" variant="body2">
                ✅ <strong>Builds trust</strong>: Shows system is working to eliminate all repeat partnerships
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Layout>
  );
};

export default PartnershipSwapSettings;
